import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { isGoneUrl, resolveLegacyRedirect } from "./lib/legacy-redirects";
import { attachSupabaseAuthLazy } from "@/lib/auth-attacher-lazy";

/**
 * Anciennes URLs WordPress : on répond en un seul saut (301 direct),
 * y compris quand elles portent un slash final — sinon le routeur
 * insère d'abord un 307 de normalisation, ce que Google pénalise.
 */
const legacyRedirectMiddleware = createMiddleware().server(async ({ next, request }) => {
  const { pathname, search } = new URL(request.url);
  if (pathname.startsWith("/lovable/")) return next();

  // Ancienne URL connue (y compris en casse mixte, ex. /Dj_Mariage.N.htm) :
  // on répond en un seul saut, avant toute normalisation de casse.
  if (isGoneUrl(pathname)) {
    return new Response("410 Gone", {
      status: 410,
      headers: { "content-type": "text/plain; charset=utf-8" },
    });
  }
  const legacyTarget = resolveLegacyRedirect(pathname);
  if (legacyTarget) {
    return new Response(null, { status: 301, headers: { location: legacyTarget + search } });
  }

  // Normalisation de la casse : /DJ-Mariage → 301 vers /dj-mariage
  if (
    !pathname.startsWith("/api/") &&
    !pathname.startsWith("/_") &&
    !pathname.startsWith("/@") &&
    !pathname.startsWith("/assets/") &&
    !pathname.startsWith("/__l5e/") &&
    !pathname.startsWith("/.") &&
    pathname !== pathname.toLowerCase()
  ) {
    return new Response(null, {
      status: 301,
      headers: { location: pathname.toLowerCase() + search },
    });
  }


  if (pathname !== "/" && pathname.endsWith("/")) {
    if (isGoneUrl(pathname)) {
      return new Response("410 Gone", {
        status: 410,
        headers: { "content-type": "text/plain; charset=utf-8" },
      });
    }
    const target = resolveLegacyRedirect(pathname);
    if (target) {
      return new Response(null, { status: 301, headers: { location: target + search } });
    }
  }
  return next();
});


const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * Le chemin critique mobile ne doit pas être encombré par une dizaine de
 * `<link rel="modulepreload">` qui se disputent la bande passante avec
 * l'image LCP. On garde le premier (chunk d'entrée) et on supprime le reste :
 * ces modules restent chargés normalement par le graphe d'imports.
 */
const trimModulePreloadMiddleware = createMiddleware().server(async ({ next }) => {
  const result = await next();
  const response = result as unknown as Response;
  if (!(response instanceof Response)) return result;
  const rewriter = (globalThis as { HTMLRewriter?: new () => any }).HTMLRewriter;
  if (!rewriter) return result;
  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("text/html")) return result;

  let kept = 0;
  return new rewriter()
    .on('link[rel="modulepreload"]', {
      element(el: { remove: () => void }) {
        kept += 1;
        if (kept > 1) el.remove();
      },
    })
    .transform(response);
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuthLazy],
  requestMiddleware: [errorMiddleware, legacyRedirectMiddleware, trimModulePreloadMiddleware],
}));

